import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import tiffinAdminService from '../services/tiffinAdminService';

const TiffinAdmin = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Menu state
  const [menus, setMenus] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuForm, setMenuForm] = useState({ menuDate: '', menuTitle: '', isActive: true });
  const [showMenuForm, setShowMenuForm] = useState(false);

  // Items state
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemForm, setItemForm] = useState({
    tiffinMenuItem_Id: 0, itemName: '', itemDescription: '', price: '', isAvailable: true, sortOrder: 0, imageUrl: '',
  });
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Image upload
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');

  // Bulk year generation
  const [showBulkGen, setShowBulkGen] = useState(false);
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [bulkType, setBulkType] = useState('monthly');
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genTotal, setGenTotal] = useState(0);

  // Feedback
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchMenus();
  }, []);

  const showMsg = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const fetchMenus = async () => {
    setMenuLoading(true);
    try {
      const data = await tiffinAdminService.getMenus();
      setMenus(data);
    } catch (err) {
      showMsg('Failed to load menus', 'error');
    } finally {
      setMenuLoading(false);
    }
  };

  const fetchItems = async (menuId) => {
    setItemsLoading(true);
    try {
      const data = await tiffinAdminService.getMenuItems(menuId);
      setItems(data);
    } catch (err) {
      showMsg('Failed to load items', 'error');
    } finally {
      setItemsLoading(false);
    }
  };

  const handleSelectMenu = (menu) => {
    setSelectedMenu(menu);
    fetchItems(menu.tiffinMenu_Id);
    setShowItemForm(false);
    setEditingItem(null);
  };

  // Menu CRUD
  const handleSaveMenu = async (e) => {
    e.preventDefault();
    try {
      await tiffinAdminService.upsertMenu({
        tiffinMenu_Id: selectedMenu?.tiffinMenu_Id || 0,
        menuDate: menuForm.menuDate,
        menuTitle: menuForm.menuTitle,
        isActive: menuForm.isActive,
        organization_Id: 3,
        createdBy: 'admin',
      });
      showMsg(selectedMenu ? 'Menu updated!' : 'Menu created!');
      setShowMenuForm(false);
      setMenuForm({ menuDate: '', menuTitle: '', isActive: true });
      fetchMenus();
    } catch (err) {
      showMsg('Failed to save menu', 'error');
    }
  };

  const openNewMenu = () => {
    setSelectedMenu(null);
    setMenuForm({ menuDate: new Date().toISOString().split('T')[0], menuTitle: '', isActive: true });
    setShowMenuForm(true);
  };

  const openEditMenu = (menu) => {
    setSelectedMenu(menu);
    setMenuForm({
      menuDate: menu.menuDate?.split('T')[0] || '',
      menuTitle: menu.menuTitle || '',
      isActive: menu.isActive,
    });
    setShowMenuForm(true);
  };

  // Item CRUD
  const handleSaveItem = async (e) => {
    e.preventDefault();
    if (!selectedMenu) return;
    try {
      await tiffinAdminService.upsertMenuItem({
        tiffinMenuItem_Id: editingItem?.tiffinMenuItem_Id || 0,
        tiffinMenu_Id: selectedMenu.tiffinMenu_Id,
        itemName: itemForm.itemName,
        itemDescription: itemForm.itemDescription,
        price: parseFloat(itemForm.price) || 0,
        isAvailable: itemForm.isAvailable,
        sortOrder: parseInt(itemForm.sortOrder) || 0,
        imageUrl: itemForm.imageUrl,
      });
      showMsg(editingItem ? 'Item updated!' : 'Item added!');
      setShowItemForm(false);
      setEditingItem(null);
      resetItemForm();
      fetchItems(selectedMenu.tiffinMenu_Id);
    } catch (err) {
      showMsg('Failed to save item', 'error');
    }
  };

  const resetItemForm = () => {
    setItemForm({ tiffinMenuItem_Id: 0, itemName: '', itemDescription: '', price: '', isAvailable: true, sortOrder: 0, imageUrl: '' });
    setImagePreview('');
  };

  const openNewItem = () => {
    setEditingItem(null);
    resetItemForm();
    setShowItemForm(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      tiffinMenuItem_Id: item.tiffinMenuItem_Id,
      itemName: item.itemName || '',
      itemDescription: item.itemDescription || '',
      price: item.price?.toString() || '',
      isAvailable: item.isAvailable,
      sortOrder: item.sortOrder || 0,
      imageUrl: item.imageUrl || '',
    });
    setImagePreview(item.imageUrl || '');
    setShowItemForm(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await tiffinAdminService.deleteMenuItem(itemId);
      showMsg('Item deleted!');
      fetchItems(selectedMenu.tiffinMenu_Id);
    } catch (err) {
      showMsg('Failed to delete item', 'error');
    }
  };

  // Image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);

      const result = await tiffinAdminService.uploadImage(file);
      setItemForm(prev => ({ ...prev, imageUrl: result.imageUrl }));
      showMsg('Image uploaded!');
    } catch (err) {
      showMsg('Image upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkGenerate = async () => {
    const year = parseInt(bulkYear);
    const menus = [];
    if (bulkType === 'monthly') {
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      for (let m = 0; m < 12; m++) {
        menus.push({ menuDate: `${year}-${String(m+1).padStart(2,'0')}-01`, menuTitle: `${months[m]} ${year}` });
      }
    } else {
      let d = new Date(`${year}-01-01`);
      while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
      let week = 1;
      while (d.getFullYear() === year) {
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        menus.push({ menuDate: d.toISOString().split('T')[0], menuTitle: `Week ${week} - ${label} ${year}` });
        d.setDate(d.getDate() + 7);
        week++;
      }
    }
    setGenTotal(menus.length);
    setGenProgress(0);
    setGenerating(true);
    let created = 0;
    for (const m of menus) {
      try {
        await tiffinAdminService.upsertMenu({ tiffinMenu_Id: 0, menuDate: m.menuDate, menuTitle: m.menuTitle, isActive: true, organization_Id: 3, createdBy: 'admin' });
        created++;
      } catch (err) { console.error('Failed:', m.menuTitle, err); }
      setGenProgress(created);
    }
    setGenerating(false);
    setShowBulkGen(false);
    showMsg(`✅ Created ${created} of ${menus.length} menus for ${year}!`);
    fetchMenus();
  };

  const inputCls = 'w-full px-3 py-2.5 text-sm rounded-lg border border-stone-200 focus:border-amber-600 focus:ring-1 focus:ring-amber-200 outline-none transition bg-white';
  const btnPrimary = 'bg-gradient-to-b from-amber-700 to-amber-800 hover:from-amber-800 hover:to-amber-900 text-white font-semibold py-2.5 px-5 rounded-lg transition-all text-sm';
  const btnSecondary = 'bg-white hover:bg-stone-50 text-stone-600 font-semibold py-2.5 px-5 rounded-lg border border-stone-200 transition-all text-sm';

  return (
    <div className="min-h-screen bg-stone-50/50">
      {/* Header */}
      <section className="relative bg-white border-b border-stone-200" style={{boxShadow: '0 1px 8px rgba(120,80,40,0.06)'}}>
        <div className="container-custom py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="w-10 h-10 rounded-full bg-stone-50 hover:bg-stone-100 flex items-center justify-center border border-stone-200" style={{boxShadow: '0 2px 6px rgba(120,80,40,0.08)'}}>
                <svg className="w-5 h-5 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-stone-800">Tiffin Menu Admin</h1>
                <p className="text-stone-400 text-sm mt-1">Manage daily menus and items</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowBulkGen(v => !v)} className={btnSecondary}>
                📅 Generate Year
              </button>
              <button onClick={openNewMenu} className={btnPrimary} style={{boxShadow: '0 4px 12px rgba(120,80,40,0.2)'}}>
                + New Menu
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Bulk Generate Panel */}
      {showBulkGen && (
        <div className="container-custom mt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" style={{boxShadow: '0 2px 12px rgba(120,80,40,0.08)'}}>
            <h3 className="font-bold text-amber-900 mb-3">📅 Generate Year Menus</h3>
            {generating ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-200 border-t-amber-700 mx-auto mb-2"></div>
                <p className="text-amber-800 font-semibold text-sm">Creating menus... {genProgress} / {genTotal}</p>
                <div className="w-full bg-amber-100 rounded-full h-2 mt-3">
                  <div className="bg-amber-700 h-2 rounded-full transition-all" style={{width: genTotal ? `${(genProgress/genTotal)*100}%` : '0%'}}></div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-xs font-semibold text-amber-800 mb-1">Year</label>
                  <input type="number" min="2024" max="2030" value={bulkYear}
                    onChange={e => setBulkYear(e.target.value)}
                    className="w-28 px-3 py-2 text-sm rounded-lg border border-amber-300 focus:border-amber-600 outline-none bg-white font-bold text-amber-900" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-amber-800 mb-1">Frequency</label>
                  <div className="flex gap-2">
                    <button onClick={() => setBulkType('monthly')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${bulkType === 'monthly' ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'}`}>
                      Monthly (12)
                    </button>
                    <button onClick={() => setBulkType('weekly')}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${bulkType === 'weekly' ? 'bg-amber-700 text-white border-amber-700' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'}`}>
                      Weekly (52)
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleBulkGenerate}
                    className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-2 px-5 rounded-lg text-sm transition">
                    Generate {bulkType === 'monthly' ? '12' : '52'} Menus
                  </button>
                  <button onClick={() => setShowBulkGen(false)}
                    className="bg-white hover:bg-stone-50 text-stone-500 font-semibold py-2 px-4 rounded-lg text-sm border border-stone-200 transition">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Feedback message */}
      {message.text && (
        <div className={`container-custom mt-4`}>
          <div className={`p-3 rounded-lg text-sm font-medium ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {message.text}
          </div>
        </div>
      )}

      <div className="container-custom py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left: Menu List */}
          <div className="lg:col-span-1">
            <h2 className="text-sm font-bold text-stone-500 uppercase tracking-wider mb-4">Menus</h2>

            {menuLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-amber-200 border-t-amber-700 mx-auto"></div>
              </div>
            ) : menus.length === 0 ? (
              <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
                <p className="text-stone-400 text-sm">No menus yet. Create your first menu!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {menus.map((menu) => (
                  <div
                    key={menu.tiffinMenu_Id}
                    onClick={() => handleSelectMenu(menu)}
                    className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
                      selectedMenu?.tiffinMenu_Id === menu.tiffinMenu_Id
                        ? 'border-amber-700/30 ring-1 ring-amber-100/50'
                        : 'border-stone-200 hover:border-stone-300'
                    }`}
                    style={{boxShadow: selectedMenu?.tiffinMenu_Id === menu.tiffinMenu_Id ? '0 4px 15px rgba(120,80,40,0.1)' : '0 1px 4px rgba(0,0,0,0.04)'}}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-stone-800 text-sm">{menu.menuTitle || 'Untitled Menu'}</p>
                        <p className="text-stone-400 text-xs mt-0.5">
                          {new Date(menu.menuDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${menu.isActive ? 'bg-green-50 text-green-700' : 'bg-stone-100 text-stone-400'}`}>
                          {menu.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span className="text-xs text-stone-400">{menu.itemCount} items</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditMenu(menu); }}
                          className="w-7 h-7 rounded-md bg-stone-50 hover:bg-stone-100 flex items-center justify-center text-stone-400 hover:text-stone-600 transition"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Items or Menu Form */}
          <div className="lg:col-span-2">

            {/* Menu Form Modal */}
            {showMenuForm && (
              <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6" style={{boxShadow: '0 4px 15px rgba(120,80,40,0.08)'}}>
                <h3 className="font-bold text-stone-800 mb-4">{selectedMenu ? 'Edit Menu' : 'New Menu'}</h3>
                <form onSubmit={handleSaveMenu} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-stone-500 mb-1">Menu Date *</label>
                      <input type="date" required value={menuForm.menuDate} onChange={(e) => setMenuForm(p => ({...p, menuDate: e.target.value}))} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-stone-500 mb-1">Title *</label>
                      <input type="text" required placeholder="Today's Special Menu" value={menuForm.menuTitle} onChange={(e) => setMenuForm(p => ({...p, menuTitle: e.target.value}))} className={inputCls} />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={menuForm.isActive} onChange={(e) => setMenuForm(p => ({...p, isActive: e.target.checked}))} className="w-4 h-4 rounded border-stone-300 text-amber-700 focus:ring-amber-200" />
                    <span className="text-sm text-stone-600">Active (visible to customers)</span>
                  </label>
                  <div className="flex gap-3">
                    <button type="submit" className={btnPrimary}>Save Menu</button>
                    <button type="button" onClick={() => setShowMenuForm(false)} className={btnSecondary}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {/* Items Section */}
            {selectedMenu && !showMenuForm && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-bold text-stone-500 uppercase tracking-wider">Menu Items</h2>
                    <p className="text-xs text-stone-400 mt-0.5">{selectedMenu.menuTitle} — {new Date(selectedMenu.menuDate).toLocaleDateString()}</p>
                  </div>
                  <button onClick={openNewItem} className={btnPrimary} style={{boxShadow: '0 4px 12px rgba(120,80,40,0.2)'}}>
                    + Add Item
                  </button>
                </div>

                {/* Item Form */}
                {showItemForm && (
                  <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6" style={{boxShadow: '0 4px 15px rgba(120,80,40,0.08)'}}>
                    <h3 className="font-bold text-stone-800 mb-4">{editingItem ? 'Edit Item' : 'New Item'}</h3>
                    <form onSubmit={handleSaveItem} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-stone-500 mb-1">Item Name *</label>
                          <input type="text" required placeholder="Dal Makhani" value={itemForm.itemName} onChange={(e) => setItemForm(p => ({...p, itemName: e.target.value}))} className={inputCls} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">Price ($) *</label>
                            <input type="number" step="0.01" required placeholder="8.00" value={itemForm.price} onChange={(e) => setItemForm(p => ({...p, price: e.target.value}))} className={inputCls} />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-stone-500 mb-1">Sort Order</label>
                            <input type="number" value={itemForm.sortOrder} onChange={(e) => setItemForm(p => ({...p, sortOrder: e.target.value}))} className={inputCls} />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Description</label>
                        <input type="text" placeholder="Creamy black lentils slow-cooked with butter and spices" value={itemForm.itemDescription} onChange={(e) => setItemForm(p => ({...p, itemDescription: e.target.value}))} className={inputCls} />
                      </div>

                      {/* Image Upload */}
                      <div>
                        <label className="block text-xs font-semibold text-stone-500 mb-1">Item Image</label>
                        <div className="flex items-start gap-4">
                          {(imagePreview || itemForm.imageUrl) && (
                            <div className="w-20 h-20 rounded-lg overflow-hidden border border-stone-200 flex-shrink-0">
                              <img src={imagePreview || itemForm.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div className="flex-1">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                              className={`${btnSecondary} ${uploading ? 'opacity-50' : ''}`}
                            >
                              {uploading ? (
                                <span className="flex items-center gap-2">
                                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-stone-300 border-t-stone-600"></div>
                                  Uploading...
                                </span>
                              ) : (
                                'Choose Image'
                              )}
                            </button>
                            {itemForm.imageUrl && (
                              <button
                                type="button"
                                onClick={() => { setItemForm(p => ({...p, imageUrl: ''})); setImagePreview(''); }}
                                className="ml-2 text-red-500 hover:text-red-700 text-xs font-medium"
                              >
                                Remove
                              </button>
                            )}
                            <p className="text-[11px] text-stone-400 mt-1">JPG, PNG, GIF, WebP. Saved to Azure Blob Storage.</p>
                          </div>
                        </div>
                      </div>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={itemForm.isAvailable} onChange={(e) => setItemForm(p => ({...p, isAvailable: e.target.checked}))} className="w-4 h-4 rounded border-stone-300 text-amber-700 focus:ring-amber-200" />
                        <span className="text-sm text-stone-600">Available</span>
                      </label>
                      <div className="flex gap-3">
                        <button type="submit" className={btnPrimary}>Save Item</button>
                        <button type="button" onClick={() => { setShowItemForm(false); setEditingItem(null); resetItemForm(); }} className={btnSecondary}>Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Items List */}
                {itemsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-amber-200 border-t-amber-700 mx-auto"></div>
                  </div>
                ) : items.length === 0 ? (
                  <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
                    <div className="text-4xl mb-3">🍽️</div>
                    <p className="text-stone-400 text-sm">No items in this menu yet. Add your first item!</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item, idx) => (
                      <div
                        key={item.tiffinMenuItem_Id}
                        className="bg-white rounded-xl p-4 border border-stone-200 flex items-center gap-4 transition-all hover:border-stone-300"
                        style={{boxShadow: '0 1px 4px rgba(0,0,0,0.04)'}}
                      >
                        {/* Image or placeholder */}
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0 border border-stone-200">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.itemName} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-300 text-lg">
                              🍛
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-stone-400 bg-stone-50 px-1.5 py-0.5 rounded">{idx + 1}</span>
                            <p className="font-bold text-stone-800 text-sm truncate">{item.itemName}</p>
                            {!item.isAvailable && (
                              <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Unavailable</span>
                            )}
                          </div>
                          {item.itemDescription && (
                            <p className="text-xs text-stone-400 mt-0.5 truncate">{item.itemDescription}</p>
                          )}
                        </div>

                        {/* Price */}
                        <p className="font-extrabold text-amber-800 text-sm flex-shrink-0">${item.price?.toFixed(2)}</p>

                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => openEditItem(item)}
                            className="w-8 h-8 rounded-md bg-stone-50 hover:bg-amber-50 flex items-center justify-center text-stone-400 hover:text-amber-700 transition"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.tiffinMenuItem_Id)}
                            className="w-8 h-8 rounded-md bg-stone-50 hover:bg-red-50 flex items-center justify-center text-stone-400 hover:text-red-600 transition"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* No menu selected */}
            {!selectedMenu && !showMenuForm && (
              <div className="bg-white rounded-xl border border-stone-200 p-16 text-center" style={{boxShadow: '0 2px 8px rgba(0,0,0,0.03)'}}>
                <div className="text-5xl mb-4">📋</div>
                <h3 className="text-lg font-bold text-stone-700 mb-1">Select a Menu</h3>
                <p className="text-stone-400 text-sm">Choose a menu from the left to manage its items, or create a new one.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiffinAdmin;
